import React, { Fragment, useEffect, useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "@component/AppProvider";
import parse, { domToReact } from "html-react-parser";
import swal from "sweetalert";
import moment from "moment";

import _API from "@util/api";
import _U from "@util/utilities";
import _DEF from "@config/defines";
import _CK from "@util/cookie";

import Editor from "@component/UI/TMEditor";

import Loading from "@component/UI/Loading";

export default () => {
    const navigate = useNavigate();
    const { idx } = useParams();

    const { site, iam } = useContext(AppContext);
    const [cfg, setCfg] = useState(null);
    const [data, setData] = useState({});
    const [fix, setFix] = useState([]);
    const [add, setAdd] = useState([]);
    const [topic, setTopic] = useState([]);
    const [detail, setDetail] = useState(null);
    const [awsKey, setAwsKey] = useState(null);

    const [isLoading, setIsLoading] = useState(true);

    const loadPrepare = () => {
        _API.loadUploadKeys(true)
            .then((res) => res.data)
            .then((data) => setAwsKey(data));
    };

    useEffect(() => {
        loadPrepare();
    }, []);

    const loadConfig = () => {
        _API.get({ path: "/site/abstract/config" })
            .then((res) => {
                const { cfg, fixOptions, addOptions, topicOptions, detailOption } = res.data;
                setCfg(cfg);
                setFix(fixOptions);
                setAdd(addOptions);
                setTopic(topicOptions);
                setDetail(detailOption);
            })
            .catch((e) => {
                _API.alert({ message: e.message }).then(() => {
                    navigate("/");
                });
            });
    };

    useEffect(() => {
        if (site?.siteCode) {
            loadConfig();
        }
    }, [site?.siteCode]);

    const loadData = () => {
        _API.get({ path: "/site/abstract/item", data: { idx } })
            .then((res) => {
                const data = res.data;
                if (!data?.signuture) {
                    data.signuture = iam?.nameFirst + " " + iam?.nameLast;
                }
                if (!data?.signutureDate) {
                    data.signutureDate = moment().format("YYYY-MM-DD");
                } else {
                    data.signutureDate = moment(data.signutureDate).format("YYYY-MM-DD");
                }

                setData(data);
                setIsLoading(false);
            })
            .catch((e) => {
                _API.alert({ message: e.message }).then(() => {
                    navigate("/");
                });
            });
    };

    useEffect(() => {
        if (cfg && idx) {
            loadData();
        }
    }, [cfg, idx]);

    useEffect(() => {
        if (cfg) {
            if (!cfg?.startDate || !cfg?.endDate) {
                swal("Please contact the central office (" + site?.emailAbstract + ") for the abstract submission.").then(() => {
                    navigate("/");
                });
                return;
            }
            if (moment() < moment(cfg?.startDate) || moment() > moment(cfg?.endDate)) {
                swal("Please contact the central office (" + site?.emailAbstract + ") for the abstract submission.").then(() => {
                    navigate("/");
                });
                return;
            }
            setIsLoading(false);
        }
    }, [cfg, data]);

    const findFixItem = (type) => {
        const item = fix.find((f) => f.type === type);
        if (item) {
            return item;
        }

        return false;
    };

    const onAddDataChange = (e) => {
        if (e.target.type === "checkbox") {
            let tmp = data?.metadata?.[e.target.name] ? JSON.parse(JSON.stringify(data?.metadata?.[e.target.name])) : [];
            if (e.target.checked) {
                tmp.push(e.target.value);
            } else {
                tmp = tmp.filter((t) => t !== e.target.value);
            }
            setData({ ...data, metadata: { ...data?.metadata, [e.target.name]: tmp } });
        } else {
            setData({ ...data, metadata: { ...data?.metadata, [e.target.name]: e.target.value } });
        }
    };

    const onDataChange = (e) => {
        if (e.target.name === "topicFirst" && data?.topicSecond === e.target.value) {
            swal("Please choose another Topic.");
            return;
        }
        if (e.target.name === "topicSecond" && data?.topicFirst === e.target.value) {
            swal("Please choose another Topic.");
            return;
        }

        setData({ ...data, [e.target.name]: e.target.value });
    };

    useEffect(() => {
        if (findFixItem("ABSTRACT_DETAIL")?.isUseEditor === "Y") {
            setData({ ...data, totalWordCnt: (data?.content1Length || 0) + (data?.content2Length || 0) + (data?.content3Length || 0) + (data?.content4Length || 0) });
        } else {
            const count1 = data?.content1?.split(" ")?.length || 0;
            const count2 = data?.content2?.split(" ")?.length || 0;
            const count3 = data?.content3?.split(" ")?.length || 0;
            const count4 = data?.content4?.split(" ")?.length || 0;

            setData({ ...data, totalWordCnt: count1 + count2 + count3 + count4 });
        }
    }, [data?.content1, data?.content2, data?.content3, data?.content4]);

    const getTopic = (item) => {
        if (!item.options) {
            return;
        }

        const options = item.options.split("\n");
        const result = [];
        for (let i in options) {
            const o = options[i];
            result.push(
                <tr key={i}>
                    <td style={{ textAlign: "left" }}>{o}</td>
                    <td>
                        <input type="radio" value={o} name="topicFirst" checked={data?.topicFirst === o} onChange={onDataChange} />
                    </td>
                    {cfg?.isUseTopicSecond === "Y" && (
                        <td>
                            <input type="radio" value={o} name="topicSecond" checked={data?.topicSecond === o} onChange={onDataChange} />
                        </td>
                    )}
                </tr>
            );
        }

        return result;
    };

    const getDetailOption = (type) => {
        switch (type) {
            case "WORD-LIMIT":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.caseWordLimit;
                } else {
                    return detail?.abstractWordLimit;
                }
            case "BACKGROUND-TITLE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.caseBackgroundTitle;
                } else {
                    return detail?.abstractBackgroundTitle;
                }
            case "CONCLUSIONS-TITLE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.caseConclusionsTitle;
                } else {
                    return detail?.abstractConclusionsTitle;
                }
            case "METHOD-TITLE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.caseMethodTitle;
                } else {
                    return detail?.abstractMethodTitle;
                }
            case "RESULTS-TITLE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.caseResultsTitle;
                } else {
                    return detail?.abstractResultsTitle;
                }
            case "BACKGROUND-USE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.isUseCaseBackground;
                } else {
                    return detail?.isUseAbstractBackground;
                }
            case "CONCLUSIONS-USE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.isUseCaseConclusions;
                } else {
                    return detail?.isUseAbstractConclusions;
                }
            case "METHOD-USE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.isUseCaseMethod;
                } else {
                    return detail?.isUseAbstractMethod;
                }
            case "RESULTS-USE":
                if (data?.abstractType === detail?.caseReportTitle) {
                    return detail?.isUseCaseResult;
                } else {
                    return detail?.isUseAbstractResults;
                }
        }
    };

    const onDetailDataChange = (value, length, key) => {
        setData({ ...data, [key]: value, [key + "Length"]: length });
    };

    const onFileUpload = (e, num) => {
        setIsLoading(true);
        const file = e.target.files[0];

        if (!file) {
            swal("파일을 선택해 주세요.");
            return;
        }

        const realFileName = `${site?.siteCode}_${iam?.id}_abs_file${num + 1}`;

        const req = { aws: awsKey, file, siteCode: site?.siteCode, realFileName };
        _API.upload(req)
            .then((res) => res.data)
            .then((file) => {
                let files = data?.files ? JSON.parse(JSON.stringify(data?.files)) : [];
                files[num] = { fileIdx: file.id, signedUrl: file.signedUrl, fileName: file.name };

                setData({ ...data, files });
                setIsLoading(false);
            })
            .catch((e) => {
                swal(e.message);
            });
    };

    const onKeywordChange = (e, num) => {
        let keyword = data?.keyword ? JSON.parse(JSON.stringify(data?.keyword)) : [];
        keyword[num] = _U.capitalize(e.target.value);

        setData({ ...data, keyword });
    };

    const onAuthorCountChange = (e) => {
        const authorAff = data?.authorAff ? JSON.parse(JSON.stringify(data?.authorAff)) : [];
        let authorInformation = data?.authorInformation ? JSON.parse(JSON.stringify(data?.authorInformation)) : [];

        if (authorAff?.length === 0) {
            authorAff[0] = { department: _U.capitalize(iam?.metadata?.department), inst: _U.capitalize(iam?.metadata?.organ), city: _U.capitalize(iam?.metadata?.city), country: iam?.country };
        }

        const num = e.target.value;

        for (let i = 0; i < num; i++) {
            if (i == 0) {
                authorInformation[i] = {
                    title: _U.capitalize(authorInformation?.[i]?.title || iam?.metadata?.title),
                    nameFirst: _U.capitalize(authorInformation?.[i]?.nameFirst || iam?.nameFirst),
                    nameLast: _U.allUppercase(authorInformation?.[i]?.nameLast || iam?.nameLast),
                    email: authorInformation?.[i]?.email || iam?.email,
                    aff: 0,
                    present: "N",
                    corresponding: "Y",
                };
            } else {
                authorInformation[i] = {
                    title: null,
                    nameFirst: null,
                    nameLast: null,
                    email: null,
                    aff: 0,
                    present: "N",
                    corresponding: "N",
                };
            }
        }

        authorInformation = authorInformation.filter((i, j) => j < parseInt(num));

        setData({ ...data, [e.target.name]: e.target.value, authorAff, authorInformation });
    };

    const onAuthorDataChange = (e, num) => {
        const authorInformation = data?.authorInformation ? JSON.parse(JSON.stringify(data?.authorInformation)) : [];

        if (e.target.name === "present" || e.target.name === "corresponding") {
            authorInformation.map((t) => (t[e.target.name] = "N"));
        }

        if (e.target.name === "nameFirst") {
            authorInformation[num][e.target.name] = _U.capitalize(e.target.value);
        } else if (e.target.name === "nameLast") {
            authorInformation[num][e.target.name] = _U.allUppercase(e.target.value);
        } else {
            authorInformation[num][e.target.name] = e.target.value;
        }

        setData({ ...data, authorInformation });
    };

    const onAuthorClear = (num) => {
        const authorInformation = data?.authorInformation ? JSON.parse(JSON.stringify(data?.authorInformation)) : [];
        authorInformation[num] = {
            ...authorInformation[num],
            title: null,
            nameFirst: null,
            nameLast: null,
            email: null,
            present: "N",
            corresponding: "N",
        };

        setData({ ...data, authorInformation });
    };

    const onAffDataChange = (e, num) => {
        const authorAff = data?.authorAff ? JSON.parse(JSON.stringify(data?.authorAff)) : [];
        authorAff[num][e.target.name] = _U.capitalize(e.target.value);
        setData({ ...data, authorAff });
    };

    const onAddAff = () => {
        const authorAff = data?.authorAff ? JSON.parse(JSON.stringify(data?.authorAff)) : [];
        authorAff.push({ department: null, inst: null, city: null, country: null });
        setData({ ...data, authorAff });
    };

    const onRemoveAff = () => {
        const authorAff = data?.authorAff ? JSON.parse(JSON.stringify(data?.authorAff)) : [];
        if (authorAff?.length < 2) {
            swal("You can not delete everything.");
            return;
        }
        authorAff.splice(authorAff.length - 1, 1);
        setData({ ...data, authorAff });
    };

    const onSave = () => {
        if (!data?.abstractType) {
            swal("Please choice abstract type.");
            return;
        }

        for (let i in add) {
            const a = add[i];
            if (a.isMust === "Y" && !data?.metadata?.[a.name]) {
                if (a.type === "RADIO" || a.type === "CHECK" || a.type === "SELECT") {
                    swal(`Please choice ${a.title}`);
                    return;
                } else {
                    swal(`Please enter ${a.title}`);
                    return;
                }
            }
        }

        if (findFixItem("TITLE") && findFixItem("TITLE")?.isMust === "Y" && !data?.title) {
            swal("Please enter title.");
            return;
        }

        if (findFixItem("ABSTRACT_DETAIL") && findFixItem("ABSTRACT_DETAIL")?.isMust === "Y") {
            if (getDetailOption("BACKGROUND-USE") === "Y" && !data?.content1) {
                swal(`Please enter ${getDetailOption("BACKGROUND-TITLE")}.`);
                return;
            }
            if (getDetailOption("METHOD-USE") === "Y" && !data?.content2) {
                swal(`Please enter ${getDetailOption("METHOD-TITLE")}.`);
                return;
            }
            if (getDetailOption("RESULTS-USE") === "Y" && !data?.content3) {
                swal(`Please enter ${getDetailOption("RESULTS-TITLE")}.`);
                return;
            }
            if (getDetailOption("CONCLUSIONS-USE") === "Y" && !data?.content4) {
                swal(`Please enter ${getDetailOption("CONCLUSIONS-TITLE")}.`);
                return;
            }

            if (parseInt(data?.totalWordCnt || 0) > parseInt(getDetailOption("WORD-LIMIT") || 0)) {
                swal(`Abstracts should NOT exceed ${getDetailOption("WORD-LIMIT")} words.`);
                return;
            }
        }
        if (findFixItem("FILE") && findFixItem("FILE")?.isMust == "Y" && (data?.files?.length || 0) < parseInt(findFixItem("FILE")?.value || 1)) {
            swal(`Please attach files.`);
            return;
        }

        if (findFixItem("CONFIRM") && findFixItem("CONFIRM")?.isMust == "Y" && data?.agreement !== "Y") {
            swal(`Please tick the box if this submission has been approved by all authors.`);
            return;
        }

        if (findFixItem("KEYWORD") && findFixItem("KEYWORD")?.isMust == "Y" && (data?.keyword?.length || 0) < parseInt(findFixItem("KEYWORD")?.value || 1)) {
            swal(`Please enter keywords.`);
            return;
        }

        if (cfg?.isUseTopic === "Y" && !data?.topicFirst) {
            if (cfg?.isUseTopicSecond === "Y") {
                swal(`Please choose 1st preferred topic. Choose the "2nd Option" if necessary.`);
                return;
            } else {
                swal(`Please choose preferred topic.`);
                return;
            }
        }

        if (findFixItem("AUTHOR") && findFixItem("AUTHOR")?.isMust == "Y") {
            if (!data?.authorCount) {
                swal(`Please choose number of author.`);
                return;
            }

            for (let i in data?.authorAff) {
                const af = data?.authorAff[i];
                if (!af?.department) {
                    swal(`Please enter your department.`);
                    return;
                }
                if (!af?.inst) {
                    swal(`Please enter your institution.`);
                    return;
                }
                if (!af?.city) {
                    swal(`Please enter your city, state/region.`);
                    return;
                }
                if (!af?.country) {
                    swal(`Please choose your country.`);
                    return;
                }
            }

            let presentChk = 0;
            let corresChk = 0;

            for (let i in data?.authorInformation) {
                const ai = data?.authorInformation[i];
                if (!ai?.title) {
                    swal(`Please choose your title.`);
                    return;
                }
                if (!ai?.nameFirst) {
                    swal(`Please enter your first name.`);
                    return;
                }
                if (!ai?.nameLast) {
                    swal(`Please enter your last name.`);
                    return;
                }
                if (!ai?.email) {
                    swal(`Please enter your email.`);
                    return;
                }

                if (ai?.present === "Y") {
                    presentChk++;
                }
                if (ai?.corresponding === "Y") {
                    corresChk++;
                }
            }

            if (!presentChk) {
                swal(`Please choice presenting author.`);
                return;
            }

            if (!corresChk) {
                swal(`Please choice corresponding author.`);
                return;
            }
        }

        _API.post({ path: "/site/abstract/step1", data: { ...data, abstractIdx: cfg?.idx } })
            .then((res) => {
                if (res.error === 0) {
                    if (idx) {
                        navigate(`/abstract/modify/step2/${res.data}`);
                    } else {
                        navigate(`/abstract/regist/${res.data}`);
                    }
                }
            })
            .catch((e) => {
                console.log(e);

                _API.alert({ message: e.message });
            });
    };

    if (!_CK.isLogin()) {
        swal("Please Login first.").then(() => {
            document.location.href = "/";
        });
        return;
    }

    return (
        <Fragment>
            {isLoading && <Loading />}
            <div className="contents">
                {site?.naviAbstract && parse(site?.naviAbstract)}

                <div className="right">
                    <div className="contentsTop">
                        <h3>On-line Abstract Submission</h3>
                        <div className="location">
                            <a href="/" className="home">
                                home
                            </a>
                            <div className="loc_txt">
                                <span>On-line Abstract Submission</span>
                            </div>
                        </div>
                    </div>
                    <div className="contWrap">
                        {findFixItem("ABSTRACT_TYPE") && (
                            <>
                                <h5 className="mt30">{findFixItem("ABSTRACT_TYPE")?.title}</h5>

                                <p>
                                    {findFixItem("ABSTRACT_TYPE")?.isMust === "Y" && <span className="txt_red">*</span>}
                                    {findFixItem("ABSTRACT_TYPE")?.memo && (
                                        <>
                                            &nbsp;
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: findFixItem("ABSTRACT_TYPE")?.memo,
                                                }}
                                            ></span>
                                        </>
                                    )}
                                </p>
                                <div className="box_blue">
                                    <label>
                                        <input
                                            type="radio"
                                            value={detail?.abstractTitle || "Abstract"}
                                            name="abstractType"
                                            checked={data?.abstractType === detail?.abstractTitle}
                                            onChange={(e) => setData({ ...data, abstractType: e.target.value, registrationCode: detail?.abstractCode || "A-" })}
                                        />
                                        {detail?.abstractTitle || "Abstract"}
                                    </label>
                                    {detail?.isUseCaseReport === "Y" && (
                                        <label>
                                            <input
                                                type="radio"
                                                value={detail?.caseReportTitle || "Case report"}
                                                name="abstractType"
                                                checked={data?.abstractType === detail?.caseReportTitle}
                                                onChange={(e) => setData({ ...data, abstractType: e.target.value, registrationCode: detail?.caseReportCode || "C-" })}
                                            />
                                            {detail?.caseReportTitle || "Case report"}
                                        </label>
                                    )}
                                </div>
                            </>
                        )}
                        {add?.map((item, index) => (
                            <Fragment key={index}>
                                <h5 className="mt30">{item.title}</h5>
                                <p>
                                    {item?.isMust === "Y" && <span className="txt_red">*</span>}
                                    {item?.memo && (
                                        <>
                                            &nbsp;
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: item?.memo,
                                                }}
                                            ></span>
                                        </>
                                    )}
                                </p>
                                <div className="box_blue">{_U.getInputElementMake(item, data?.metadata || "", onAddDataChange)}</div>
                            </Fragment>
                        ))}
                        {findFixItem("TITLE") && (
                            <>
                                <h5 className="mt30">{findFixItem("TITLE")?.title}</h5>
                                <p>
                                    {findFixItem("TITLE")?.isMust === "Y" && <span className="txt_red">*</span>}
                                    {findFixItem("TITLE")?.memo && (
                                        <>
                                            &nbsp;
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: findFixItem("TITLE")?.memo,
                                                }}
                                            ></span>
                                        </>
                                    )}
                                </p>
                                <div>
                                    {findFixItem("TITLE")?.isUseEditor === "Y" ? (
                                        <Editor content={data?.title?.replace(/\\/g, "")} onChange={(value) => setData({ ...data, title: value })} height={200} />
                                    ) : (
                                        <textarea name="title" value={data?.title} onChange={onDataChange} cols="100%" rows="3"></textarea>
                                    )}
                                </div>
                            </>
                        )}
                        {findFixItem("ABSTRACT_DETAIL") && (
                            <>
                                <h5 className="mt30">Abstract</h5>
                                <p>
                                    <span className="txt_red">*</span> Please Enter an Abstract of Your Paper (Max. {getDetailOption("WORD-LIMIT")} words in total).
                                    <input type="text" className="inputWord" name="totalWordCnt" value={data?.totalWordCnt || 0} readOnly /> / (Maximum {getDetailOption("WORD-LIMIT")} words)
                                </p>
                                <p></p>
                                {findFixItem("ABSTRACT_DETAIL")?.memo && (
                                    <p>
                                        <span className="txt_red">*</span>&nbsp;
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: findFixItem("ABSTRACT_DETAIL")?.memo,
                                            }}
                                        ></span>
                                    </p>
                                )}
                                <table className="tbl_abs">
                                    <colgroup>
                                        <col style={{ width: "20%" }} />
                                        <col />
                                    </colgroup>
                                    {getDetailOption("BACKGROUND-USE") === "Y" && (
                                        <tr>
                                            <th>
                                                <span className="txt_red">*</span> {getDetailOption("BACKGROUND-TITLE")}
                                            </th>
                                            <td>
                                                {findFixItem("ABSTRACT_DETAIL")?.isUseEditor === "Y" ? (
                                                    <Editor content={data?.content1?.replace(/\\/g, "")} onChange={(v, l) => onDetailDataChange(v, l, "content1")} height={200} />
                                                ) : (
                                                    <textarea cols="100%" rows="3" name="content1" value={data?.content1} onChange={onDataChange}></textarea>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {getDetailOption("METHOD-USE") === "Y" && (
                                        <tr>
                                            <th>
                                                <span className="txt_red">*</span> {getDetailOption("METHOD-TITLE")}
                                            </th>
                                            <td>
                                                {findFixItem("ABSTRACT_DETAIL")?.isUseEditor === "Y" ? (
                                                    <Editor content={data?.content2?.replace(/\\/g, "")} onChange={(v, l) => onDetailDataChange(v, l, "content2")} height={200} />
                                                ) : (
                                                    <textarea cols="100%" rows="3" name="content2" value={data?.content2} onChange={onDataChange}></textarea>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {getDetailOption("RESULTS-USE") === "Y" && (
                                        <tr>
                                            <th>
                                                <span className="txt_red">*</span> {getDetailOption("RESULTS-TITLE")}
                                            </th>
                                            <td>
                                                {findFixItem("ABSTRACT_DETAIL")?.isUseEditor === "Y" ? (
                                                    <Editor content={data?.content3?.replace(/\\/g, "")} onChange={(v, l) => onDetailDataChange(v, l, "content3")} height={200} />
                                                ) : (
                                                    <textarea cols="100%" rows="3" name="content3" value={data?.content3} onChange={onDataChange}></textarea>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    {getDetailOption("CONCLUSIONS-USE") === "Y" && (
                                        <tr>
                                            <th>
                                                <span className="txt_red">*</span> {getDetailOption("CONCLUSIONS-TITLE")}
                                            </th>
                                            <td>
                                                {findFixItem("ABSTRACT_DETAIL")?.isUseEditor === "Y" ? (
                                                    <Editor content={data?.content4?.replace(/\\/g, "")} onChange={(v, l) => onDetailDataChange(v, l, "content4")} height={200} />
                                                ) : (
                                                    <textarea cols="100%" rows="3" name="content4" value={data?.content4} onChange={onDataChange}></textarea>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </table>
                            </>
                        )}
                        {findFixItem("FILE") && (
                            <>
                                <p className="mt20">
                                    {findFixItem("FILE")?.isMust === "Y" && <span className="txt_red">*</span>}
                                    {findFixItem("FILE")?.title} (maximum {findFixItem("FILE")?.value} files)
                                </p>
                                {findFixItem("FILE")?.memo && (
                                    <>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: findFixItem("FILE")?.memo,
                                            }}
                                        ></div>
                                    </>
                                )}
                                <table className="tbl_abs">
                                    <colgroup>
                                        <col style={{ width: "20%" }} />
                                        <col />
                                    </colgroup>
                                    {[...Array(parseInt(findFixItem("FILE")?.value || 1))].map((item, index) => (
                                        <tr key={index}>
                                            <th>
                                                {findFixItem("FILE")?.isMust === "Y" && <span className="txt_red">*</span>} File {index + 1}
                                            </th>
                                            <td>
                                                <input type="file" name={`file${index + 1}`} onChange={(e) => onFileUpload(e, index)} />
                                                {data?.files?.[index] && (
                                                    <div>
                                                        Attachment {index + 1} :{" "}
                                                        <a href={data?.files[index]?.signedUrl} target="_blank">
                                                            {data?.files[index]?.fileName}
                                                        </a>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </table>
                            </>
                        )}
                        {findFixItem("CONFIRM") && (
                            <label>
                                {findFixItem("CONFIRM")?.isMust === "Y" && (
                                    <span className="txt_red" style={{ marginRight: "5px", verticalAlign: "sub" }}>
                                        *
                                    </span>
                                )}
                                <input type="checkbox" value="Y" checked={data?.agreement === "Y"} onChange={(e) => setData({ ...data, agreement: data?.agreement === "Y" ? "N" : "Y" })} /> &nbsp;
                                {findFixItem("CONFIRM")?.title}
                            </label>
                        )}
                        {findFixItem("KEYWORD") && (
                            <>
                                <h5 className="mt30">{findFixItem("KEYWORD")?.title}</h5>
                                <p>
                                    {findFixItem("KEYWORD")?.isMust === "Y" && <span className="txt_red">*</span>}
                                    {findFixItem("KEYWORD")?.memo && (
                                        <>
                                            &nbsp;
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: findFixItem("KEYWORD")?.memo,
                                                }}
                                            ></span>
                                        </>
                                    )}
                                </p>
                                <div className="box_blue">
                                    {[...Array(parseInt(findFixItem("KEYWORD")?.value || 1))].map((item, index) => (
                                        <input type="text" name={`keyword${index + 1}`} className="keyword" value={data?.keyword?.[index]} onChange={(e) => onKeywordChange(e, index)} />
                                    ))}
                                </div>
                            </>
                        )}
                        {cfg?.isUseTopic === "Y" && (
                            <Fragment>
                                <h5 className="mt30">Topic</h5>
                                {cfg?.memo && (
                                    <p>
                                        &nbsp;
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: cfg?.memo,
                                            }}
                                        ></span>
                                    </p>
                                )}
                                <div className="boxTable">
                                    <table className="online_reg_step01">
                                        <thead>
                                            <tr>
                                                <th width="60%">Topics</th>
                                                {cfg?.isUseTopicSecond === "Y" ? (
                                                    <>
                                                        <th width="20%">1st Choice</th>
                                                        <th width="20%">2nd Choice</th>
                                                    </>
                                                ) : (
                                                    <th width="40%">Choice</th>
                                                )}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {topic?.map((item, index) => (
                                                <Fragment key={index}>
                                                    <tr>
                                                        <td style={{ textAlign: "left" }} className="tit" colSpan={cfg?.isUseTopicSecond === "Y" ? 3 : 2}>
                                                            {item.title}
                                                        </td>
                                                    </tr>
                                                    {getTopic(item)}
                                                </Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </Fragment>
                        )}
                        {findFixItem("AUTHOR") && (
                            <>
                                <h5 className="mt30">{findFixItem("AUTHOR")?.title}</h5>
                                <p>
                                    Number of Author
                                    <select className="w200" name="authorCount" onChange={onAuthorCountChange} value={data?.authorCount}>
                                        <option value="">-- Select --</option>
                                        {[...Array(parseInt(findFixItem("AUTHOR")?.value || 1))].map((item, index) => (
                                            <option value={index + 1} key={index}>
                                                {index + 1}
                                            </option>
                                        ))}
                                    </select>
                                </p>
                                {data?.authorCount > 0 && (
                                    <>
                                        {data?.authorAff?.map((item, index) => (
                                            <table className="tbl_abs" key={index}>
                                                <colgroup>
                                                    <col style={{ width: "21%" }} />
                                                    <col />
                                                </colgroup>
                                                <tr>
                                                    <th rowspan="4">
                                                        Affiliation <strong className="txt_orange">{index + 1}</strong>
                                                    </th>
                                                    <th className="subhead">Department</th>
                                                    <td>
                                                        <input type="text" name="department" value={item?.department} onChange={(e) => onAffDataChange(e, index)} />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="subhead">Institution</th>
                                                    <td>
                                                        <input type="text" name="inst" value={item?.inst} onChange={(e) => onAffDataChange(e, index)} />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="subhead">City, State/Region</th>
                                                    <td>
                                                        <input type="text" name="city" value={item?.city} onChange={(e) => onAffDataChange(e, index)} />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th className="subhead">Country</th>
                                                    <td>
                                                        <select name="country" value={item?.country} onChange={(e) => onAffDataChange(e, index)}>
                                                            <option value="">COUNTRY CHOICE</option>
                                                            {_DEF.COUNTRY?.map((c, cIndex) => (
                                                                <option value={c.value} key={cIndex}>
                                                                    {c.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                </tr>
                                            </table>
                                        ))}
                                    </>
                                )}
                                <p>
                                    <span className="txt_red">*</span> Please list the institutions, hospitals or others with which the authors are affiliated.{" "}
                                </p>
                                {data?.authorCount > 0 && (
                                    <div className="">
                                        <a href="" className="btn_inner1" onClick={onAddAff}>
                                            Add Affiliation
                                        </a>
                                        <a href="" className="btn_inner1" onClick={onRemoveAff}>
                                            Delete Affiliation
                                        </a>
                                    </div>
                                )}
                                <table className="tbl_signup tbl_form mt30">
                                    <colgroup>
                                        <col style={{ width: "8%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "10%" }} />
                                        <col style={{ width: "12%" }} />
                                        <col style={{ width: "8%" }} />
                                    </colgroup>
                                    <thead>
                                        <th>Order</th>
                                        <th>Title</th>
                                        <th>First Name</th>
                                        <th>Last Name</th>
                                        <th>Email</th>
                                        <th>Affiliation</th>
                                        <th>Presenting</th>
                                        <th>Corresponding</th>
                                        <th>Clear</th>
                                    </thead>
                                    <tbody>
                                        {data?.authorInformation?.map((item, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <select name="title" value={item?.title || ""} onChange={(e) => onAuthorDataChange(e, index)}>
                                                        <option value="">Choice</option>
                                                        {_DEF.AUTHOR_TITLE?.map((t, tIndex) => (
                                                            <option value={t.value} key={tIndex}>
                                                                {t.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input type="text" name="nameFirst" value={item?.nameFirst || ""} onChange={(e) => onAuthorDataChange(e, index)} />
                                                </td>
                                                <td>
                                                    <input type="text" name="nameLast" value={item?.nameLast || ""} onChange={(e) => onAuthorDataChange(e, index)} />
                                                </td>
                                                <td>
                                                    <input type="text" name="email" value={item?.email || ""} onChange={(e) => onAuthorDataChange(e, index)} />
                                                </td>
                                                <td>
                                                    <select name="aff" value={item?.aff} onChange={(e) => onAuthorDataChange(e, index)}>
                                                        {data?.authorAff?.map((af, afIndex) => (
                                                            <option value={afIndex}>{afIndex + 1}</option>
                                                        ))}
                                                    </select>
                                                </td>
                                                <td>
                                                    <input type="radio" name="present" checked={item.present === "Y"} value="Y" onChange={(e) => onAuthorDataChange(e, index)} />
                                                </td>
                                                <td>
                                                    <input type="radio" name="corresponding" checked={item.corresponding === "Y"} value="Y" onChange={(e) => onAuthorDataChange(e, index)} />
                                                </td>
                                                <td>
                                                    <a href="" className="btn_red2" onClick={() => onAuthorClear(index)}>
                                                        Clear
                                                    </a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: findFixItem("AUTHOR")?.memo,
                                    }}
                                ></div>
                            </>
                        )}

                        <div className="btnArea1">
                            {/* <a href="javascript:history.back()" className="mgr5 btn1  btnGray1_go ">
                                Back
                            </a> */}
                            <button type="button" className=" btn1  btnBlue1_go" onClick={onSave}>
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};
